import { apiClient } from './api-client';

export interface CannedResponse {
  id: string;
  tenantId: string;
  name: string;
  shortcut?: string;
  content: string;
  category?: string;
  isPrivate: boolean;
  isEmailTemplate: boolean;
  emailSubject?: string;
  variables: string[];
  createdBy?: string;
  usageCount: number;
  isActive: boolean;
  htmlTemplateFileId?: string;
  htmlTemplateFileName?: string;
  useUploadedTemplate: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCannedResponseDto {
  name: string;
  shortcut?: string;
  content: string;
  category?: string;
  isPrivate?: boolean;
  isEmailTemplate?: boolean;
  emailSubject?: string;
  variables?: string[];
  isActive?: boolean;
  htmlTemplateFileId?: string;
  htmlTemplateFileName?: string;
  useUploadedTemplate?: boolean;
}

export interface UpdateCannedResponseDto {
  name?: string;
  shortcut?: string;
  content?: string;
  category?: string;
  isPrivate?: boolean;
  isEmailTemplate?: boolean;
  emailSubject?: string;
  variables?: string[];
  isActive?: boolean;
  htmlTemplateFileId?: string;
  htmlTemplateFileName?: string;
  useUploadedTemplate?: boolean;
}

export interface CannedResponseFilters {
  category?: string;
  isEmailTemplate?: boolean;
  isActive?: boolean;
  search?: string;
}

export interface HtmlTemplateUploadResponse {
  fileId: string;
  fileName: string;
  content: string;
}

class CannedResponsesService {
  async uploadHtmlTemplate(file: File): Promise<HtmlTemplateUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'document');
    formData.append('entityType', 'canned_response_template');

    const response = await apiClient.post<{ id: string; originalFilename: string }>('/files/upload', formData);

    // Read the file content to populate the content field
    const content = await this.readFileContent(file);

    return {
      fileId: response.data.id,
      fileName: response.data.originalFilename,
      content,
    };
  }

  private readFileContent(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string || '');
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  async getAll(filters?: CannedResponseFilters): Promise<CannedResponse[]> {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.isEmailTemplate !== undefined) params.append('isEmailTemplate', String(filters.isEmailTemplate));
    if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
    if (filters?.search) params.append('search', filters.search);

    const queryString = params.toString();
    const response = await apiClient.get<CannedResponse[]>(
      `/canned-responses${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  }

  async getEmailTemplates(): Promise<CannedResponse[]> {
    const response = await apiClient.get<CannedResponse[]>('/canned-responses/email-templates');
    return response.data;
  }

  async getById(id: string): Promise<CannedResponse> {
    const response = await apiClient.get<CannedResponse>(`/canned-responses/${id}`);
    return response.data;
  }

  async getByShortcut(shortcut: string): Promise<CannedResponse | null> {
    try {
      const response = await apiClient.get<CannedResponse | { found: false }>(`/canned-responses/shortcut/${encodeURIComponent(shortcut)}`);
      if ('found' in response.data && response.data.found === false) {
        return null;
      }
      return response.data as CannedResponse;
    } catch {
      return null;
    }
  }

  async getCategories(): Promise<string[]> {
    const response = await apiClient.get<string[]>('/canned-responses/categories');
    return response.data;
  }

  async create(dto: CreateCannedResponseDto): Promise<CannedResponse> {
    const response = await apiClient.post<CannedResponse>('/canned-responses', dto);
    return response.data;
  }

  async update(id: string, dto: UpdateCannedResponseDto): Promise<CannedResponse> {
    const response = await apiClient.put<CannedResponse>(`/canned-responses/${id}`, dto);
    return response.data;
  }

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/canned-responses/${id}`);
  }

  async incrementUsage(id: string): Promise<void> {
    await apiClient.post(`/canned-responses/${id}/increment-usage`);
  }
}

export const cannedResponsesService = new CannedResponsesService();
export default cannedResponsesService;

// Utility functions
export function extractVariables(content: string): string[] {
  // Support both {{variable}} and {variable} formats
  const doubleRegex = /\{\{([^}]+)\}\}/g;
  const singleRegex = /\{([A-Za-z][A-Za-z0-9_.]*)\}/g;
  const matches: string[] = [];
  let match;

  // Extract {{variable}} format
  while ((match = doubleRegex.exec(content)) !== null) {
    const variable = match[1].trim();
    if (!matches.includes(variable)) {
      matches.push(variable);
    }
  }

  // Extract {variable} format (only if it looks like a variable, not HTML attributes)
  while ((match = singleRegex.exec(content)) !== null) {
    const variable = match[1].trim();
    // Skip common HTML/CSS patterns
    if (!matches.includes(variable) && !['px', 'em', 'rem', 'vh', 'vw', 'none', 'auto', 'inherit'].includes(variable.toLowerCase())) {
      matches.push(variable);
    }
  }

  return matches;
}

export function replaceVariables(content: string, variables: Record<string, string>): string {
  let result = content;
  for (const [key, value] of Object.entries(variables)) {
    // Replace both {{variable}} and {variable} formats
    result = result.replace(new RegExp(`\\{\\{${escapeRegExp(key)}\\}\\}`, 'g'), value);
    result = result.replace(new RegExp(`\\{${escapeRegExp(key)}\\}`, 'g'), value);
  }
  return result;
}

// Helper to escape special regex characters
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Common variables available for email templates
// Both {{variable}} and {variable} formats are supported
export const EMAIL_TEMPLATE_VARIABLES = [
  { key: 'entity.contactName', label: 'Contact Name', description: 'Customer name from ticket' },
  { key: 'entity.contactEmail', label: 'Contact Email', description: 'Customer email from ticket' },
  { key: 'entity.ticketNumber', label: 'Ticket Number', description: 'Ticket reference number' },
  { key: 'entity.subject', label: 'Ticket Subject', description: 'Subject of the ticket' },
  { key: 'entity.organizationName', label: 'Organization Name', description: 'Your organization name' },
  { key: 'entity.failedItemsText', label: 'Failed Items (Text)', description: 'Plain text table of failed items' },
  { key: 'entity.failedItemsHtml', label: 'Failed Items (HTML)', description: 'HTML table of failed items' },
  { key: 'entity.totalFailed', label: 'Total Failed', description: 'Count of failed items' },
  // Custom variables for quote failure emails
  { key: 'BrokerName', label: 'Broker Name', description: 'Name of the broker/contact' },
  { key: 'EACSection', label: 'EAC Section', description: 'HTML section for EAC-related failures' },
  { key: 'LLFSection', label: 'LLF Section', description: 'HTML section for LLF-related failures' },
  { key: 'StartDateSection', label: 'Start Date Section', description: 'HTML section for start date failures' },
];
